import { getPlayersByPlayerIdLatestGame } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";

import { GameType, PlayerType } from "@/lib/types";

type NoDataResponse = {
  data: null;
  noDataAvailable: true;
};

const noDataMessage: Record<GameType | "both", string> = {
  game: "The data for games is not available as the player hasn't played any games yet.",
  practice:
    "The data for practice games is not available as the player hasn't played any practice games yet.",
  both: "The data is not available as the player hasn't played any games or practice games yet.",
};

/**
 * Handles API response from getPlayersByPlayerIdLatestGame
 *
 * @template T - The expected data type from the API response
 * @param res - The response object from the API call
 * @returns An object containing the data and a flag indicating if no data is available
 * @returns.data - The API response data or null
 * @returns.noDataAvailable - True if the API returned a 404 status
 * @throws Will throw the error if it's not a 404 status
 */
const handleApiResponse = <T>(res: {
  response: { status: number };
  error?: unknown;
  data?: T;
}): { data: T | null; noDataAvailable?: boolean } => {
  if (res.response.status === 404) {
    return { data: null, noDataAvailable: true };
  }
  if (res.error) {
    throw res.error;
  }
  return { data: res.data || null };
};

export const useLatestGame = (params: {
  playerId: string;
  playerType: PlayerType;
  showNoDataMessage?: boolean;
}) => {
  const { playerId, playerType, showNoDataMessage = true } = params;

  const { isLoading, data, error } = useQuery({
    queryFn: async () => {
      const [latestGame, latestPractice] = await Promise.all([
        getPlayersByPlayerIdLatestGame({
          path: { player_id: playerId },
          query: {
            gameType: "game",
            playerType,
          },
        }).then(handleApiResponse),
        getPlayersByPlayerIdLatestGame({
          path: { player_id: playerId },
          query: {
            gameType: playerType === "pitcher" ? "bullpen" : "batting_practice",
            playerType,
          },
        }).then(handleApiResponse),
      ]);

      if (
        ((latestGame as NoDataResponse).noDataAvailable ||
          (latestPractice as NoDataResponse).noDataAvailable) &&
        showNoDataMessage
      ) {
        const noGameData = (latestGame as NoDataResponse).noDataAvailable;
        const noPracticeData = (latestPractice as NoDataResponse)
          .noDataAvailable;

        toast(
          noDataMessage[
            noGameData && noPracticeData
              ? "both"
              : noGameData
                ? "game"
                : "practice"
          ],
          {
            variant: "info",
            duration: 10000,
          },
        );
      }

      return {
        latestGame: latestGame.data,
        latestPractice: latestPractice.data,
      };
    },
    queryKey: ["latest-game", playerId, playerType],
    enabled: !!playerId,
    retry: false,
  });

  const { latestGame, latestPractice } = data ?? {};

  return {
    isLoading,
    data: data ? { latestGame, latestPractice } : null,
    error,
  };
};
